import { routes } from "./routes";
import { createRoot } from "react-dom/client";
import { RouterProvider, createBrowserRouter } from "react-router-dom";
import {
  ApolloProvider,
  ApolloClient,
  InMemoryCache,
  createHttpLink,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { Observable } from "@apollo/client/utilities";
import { AuthProvider } from "@/context/auth-context";
import { ErrorBoundary } from "@components/ui/error-boundary";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./index.css";

const router = createBrowserRouter(routes);

const graphqlUri =
  import.meta.env.VITE_API_URL?.trim() || "http://localhost:8000/graphql/";

const httpLink = createHttpLink({
  uri: graphqlUri,
  credentials: "include",
  headers: {
    "apollo-require-preflight": "true",
  },
});

const PUBLIC_PATHS = [
  "/login",
  "/registrieren",
  "/register",
  "/passwort-vergessen",
  "/unternehmen-gesperrt",
];
const PUBLIC_PATH_PREFIXES = [
  "/passwort-festlegen/",
  "/passwort-zuruecksetzen/",
];

const isPublicPath = (pathname: string): boolean =>
  PUBLIC_PATHS.includes(pathname) ||
  PUBLIC_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));

let refreshPromise: Promise<boolean> | null = null;

const refreshAccessCookie = async (): Promise<boolean> => {
  const response = await fetch(graphqlUri, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      "apollo-require-preflight": "true",
    },
    body: JSON.stringify({
      query: `
        mutation RefreshCookie {
          refreshCookie
        }
      `,
      operationName: "RefreshCookie",
    }),
  });

  if (!response.ok) return false;
  const payload = await response.json();
  if (payload?.errors?.length) return false;
  return Boolean(payload?.data?.refreshCookie);
};

const errorLink = onError(
  ({ graphQLErrors, networkError, operation, forward }) => {
    const companyBlocked =
      graphQLErrors?.some(
        (e) => e.extensions?.code === "COMPANY_ACCESS_BLOCKED",
      ) ?? false;
    if (companyBlocked) {
      const { pathname } = globalThis.location;
      if (pathname !== "/unternehmen-gesperrt") {
        globalThis.location.href = "/unternehmen-gesperrt";
      }
      return;
    }

    const unauth =
      graphQLErrors?.some((e) =>
        String(e.message || "")
          .toLowerCase()
          .includes("nicht authentifiziert"),
      ) ?? false;
    const is401 =
      networkError instanceof Error &&
      "statusCode" in networkError &&
      (networkError as Error & { statusCode: unknown }).statusCode === 401;
    if (!unauth && !is401) return;

    const redirectToLogin = () => {
      const { pathname } = globalThis.location;
      if (!isPublicPath(pathname)) {
        globalThis.location.href = "/login";
      }
    };

    if (operation.operationName === "RefreshCookie") {
      redirectToLogin();
      return;
    }

    const context = operation.getContext();
    if (context?.alreadyRetried) {
      redirectToLogin();
      return;
    }

    refreshPromise ??= refreshAccessCookie()
      .catch(() => false)
      .finally(() => {
        refreshPromise = null;
      });

    return new Observable((observer) => {
      refreshPromise!
        .then((ok) => {
          if (!ok) {
            redirectToLogin();
            observer.error(new Error("Session abgelaufen."));
            return;
          }

          operation.setContext({ ...context, alreadyRetried: true });
          const subscriber = forward(operation).subscribe({
            next: (value) => observer.next(value),
            error: (err) => observer.error(err),
            complete: () => observer.complete(),
          });

          return () => subscriber.unsubscribe();
        })
        .catch((err) => observer.error(err));
    });
  },
);

const cache = new InMemoryCache({
  typePolicies: {
    User: { keyFields: ["id"] },
    Task: { keyFields: ["id"] },
    // A run keeps its identity when it gets stored: the key is built from
    // the series and the original series date, never from the task id.
    TaskOccurrence: { keyFields: ["occurrenceKey"] },
    TaskSeries: { keyFields: ["id"] },
    TaskSeriesRevision: { keyFields: ["id"] },
    TaskManagementItem: { keyFields: ["key"] },
    OccurrenceQuestion: { keyFields: false },
    SeriesQuestion: { keyFields: false },
    TaskSeriesChangePreview: { keyFields: false },
    TaskSeriesEndPreview: { keyFields: false },
    PreviewOccurrence: { keyFields: false },
    TaskCompletion: { keyFields: ["id"] },
    CostCenter: { keyFields: ["id"] },
    TimeEntry: { keyFields: ["id"] },
    TimeEvaluationEntry: { keyFields: ["id"] },
    SalaryPeriod: { keyFields: ["id"] },
    AllowancePeriod: { keyFields: ["id"] },
    LegalVacationDaysPeriod: { keyFields: ["id"] },
    SpecialVacationDaysPeriod: { keyFields: ["id"] },
    UserCostCenterAllocation: { keyFields: ["id"] },
    ReportAnswer: { keyFields: ["id"] },
    ReportQuestion: { keyFields: ["id"] },
    ReportQuestionOption: { keyFields: ["id"] },
    PlannerData: { keyFields: false },
    DaySchedule: { keyFields: false },
    DepartmentAllocation: { keyFields: false },
    CostCenterUsage: { keyFields: false },
    UserAllocationFormData: { keyFields: false },
    UserCostCenterAllocationBucket: { keyFields: false },
  },
});

const client = new ApolloClient({
  link: errorLink.concat(httpLink),
  cache,
  defaultOptions: {
    watchQuery: { fetchPolicy: "cache-and-network" },
  },
});

createRoot(document.getElementById("root")!).render(
  <ErrorBoundary>
    <ApolloProvider client={client}>
      <AuthProvider>
        <RouterProvider router={router} />
        <ToastContainer position="top-right" autoClose={4000} />
      </AuthProvider>
    </ApolloProvider>
  </ErrorBoundary>,
);
